/**
 * DayView component for calendar
 * Displays events for a single day in a timeline layout
 */

'use client';

import { darkenHexColorWithOpacity } from '@/utils/colorService';
import { Calendar, Clock, MapPin, Tag, User } from 'lucide-react';
import Link from 'next/link';
import { useMemo } from 'react';
import type { EventTeaser } from '../../types';

interface DayViewProps {
  events: EventTeaser[];
  currentDate: Date;
  baseUrl?: string;
}

export function DayView({ events, currentDate, baseUrl = '/events' }: DayViewProps) {
  // Get events for the current day
  const dayEvents = useMemo(() => {
    const targetDate = new Date(currentDate);
    targetDate.setHours(0, 0, 0, 0);

    const filtered = events.filter(event => {
      if (!event.eventDateTimeRange?.start?.timestamp) return false;

      const eventStart = new Date(event.eventDateTimeRange.start.timestamp * 1000);
      eventStart.setHours(0, 0, 0, 0);

      const eventEnd = event.eventDateTimeRange.end?.timestamp
        ? new Date(event.eventDateTimeRange.end.timestamp * 1000)
        : new Date(eventStart);
      eventEnd.setHours(23, 59, 59, 999);

      return targetDate >= eventStart && targetDate <= eventEnd;
    });

    // Sort by time
    return filtered.sort((a, b) => {
      const timeA = a.eventDateTimeRange?.start?.timestamp ?? 0;
      const timeB = b.eventDateTimeRange?.start?.timestamp ?? 0;
      return timeA - timeB;
    });
  }, [currentDate, events]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isToday = currentDate.getTime() === today.getTime();

  return (
    <div className="p-6">
      {/* Day header */}
      <div className={`mb-6 rounded-lg p-4 ${isToday ? 'bg-primary/10' : 'bg-muted/50'}`}>
        <div className="flex items-centre gap-3">
          <div
            className={`flex h-16 w-16 flex-col items-centre justify-centre rounded-lg ${
              isToday ? 'bg-primary text-primary-foreground' : 'bg-background'
            }`}
          >
            <div className={`text-xs font-medium ${isToday ? '' : 'text-muted-foreground'}`} suppressHydrationWarning>
              {currentDate.toLocaleDateString('en-GB', { month: 'short' })}
            </div>
            <div className="text-2xl font-bold" suppressHydrationWarning>{currentDate.getDate()}</div>
          </div>
          <div>
            <h3 className="text-lg font-semibold" suppressHydrationWarning>
              {currentDate.toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </h3>
            <p className="text-sm text-muted-foreground">
              {dayEvents.length} {dayEvents.length === 1 ? 'event' : 'events'}
            </p>
          </div>
        </div>
      </div>

      {/* Events list */}
      {dayEvents.length === 0 ? (
        <div className="flex min-h-[300px] flex-col items-centre justify-centre rounded-lg border border-dashed bg-muted/20 p-8 text-centre">
          <Calendar className="mb-4 h-12 w-12 text-muted-foreground/50" />
          <p className="text-lg font-medium text-muted-foreground">
            No events scheduled for this day
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {dayEvents.map(event => {
            const startTime = event.eventDateTimeRange?.start?.timestamp
              ? new Date(event.eventDateTimeRange.start.timestamp * 1000)
              : null;
            const endTime = event.eventDateTimeRange?.end?.timestamp
              ? new Date(event.eventDateTimeRange.end.timestamp * 1000)
              : startTime;

            // event.path already contains full path like /events/title
            const eventLink = event.path;

            if (!eventLink) return null;

            // Extract eventTypeColor from eventType
            const eventTypeObj = event.eventType as
              | { eventTypeColor?: string | null }
              | null
              | undefined;
            const eventTypeColor = eventTypeObj?.eventTypeColor ?? undefined;

            // Normalize eventType for display
            const normalizedTypes = event.eventType
              ? Array.isArray(event.eventType)
                ? event.eventType
                : [event.eventType]
              : [];
            const firstType = normalizedTypes[0];

            return (
              <Link
                key={event.id}
                href={eventLink}
                className={`group block rounded-lg border p-4 shadow-sm transition-all hover:shadow-md ${
                  eventTypeColor ? '' : 'bg-card'
                }`}
                style={{
                  backgroundColor: eventTypeColor
                    ? darkenHexColorWithOpacity(eventTypeColor, 80, 0.7)
                    : undefined,
                }}
              >
                <div className="flex gap-4">
                  {/* Time column */}
                  <div
                    className={`flex w-24 flex-shrink-0 flex-col items-centre justify-centre rounded-lg p-3 text-centre ${
                      eventTypeColor ? '' : 'bg-primary/10'
                    }`}
                    style={{
                      backgroundColor: eventTypeColor
                        ? darkenHexColorWithOpacity(eventTypeColor, 50, 0.3)
                        : undefined,
                    }}
                  >
                    {startTime && (
                      <>
                        <Clock
                          className="mb-1 h-4 w-4"
                          style={{ color: eventTypeColor || 'hsl(var(--primary))' }}
                        />
                        <div
                          className="text-sm font-bold"
                          style={{ color: eventTypeColor || 'hsl(var(--primary))' }}
                        >
                          {startTime.toLocaleTimeString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                        {endTime && endTime.getTime() !== startTime.getTime() && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            to{' '}
                            {endTime.toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Event details */}
                  <div className="flex-1 min-w-0">
                    <div className="mb-2 flex items-start justify-between gap-2">
                      <h4 className="text-lg font-semibold leading-tight group-hover:text-primary">
                        {event.title}
                      </h4>
                      {firstType && (
                        <span
                          className="flex-shrink-0 rounded px-2 py-1 text-xs font-medium"
                          style={{
                            backgroundColor: eventTypeColor
                              ? `${eventTypeColor}20`
                              : 'hsl(var(--primary) / 0.1)',
                            color: eventTypeColor || 'hsl(var(--primary))',
                          }}
                        >
                          {firstType.name}
                        </span>
                      )}
                    </div>

                    {event.eventLocation && (
                      <div className="mb-2 flex items-centre gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4 flex-shrink-0" />
                        <span>{event.eventLocation}</span>
                      </div>
                    )}

                    {event.body?.summary && (
                      <p className="mb-3 text-sm text-muted-foreground line-clamp-2">
                        {event.body.summary}
                      </p>
                    )}

                    {/* Metadata */}
                    <div className="flex flex-wrap items-centre gap-3 text-xs text-muted-foreground">
                      {event.theme && event.theme.length > 0 && (
                        <div className="flex items-centre gap-1">
                          <Tag className="h-3 w-3" />
                          <span>{event.theme[0].name}</span>
                          {event.theme.length > 1 && <span>+{event.theme.length - 1}</span>}
                        </div>
                      )}

                      {(event.eventContactName || event.eventContact?.name) && (
                        <div className="flex items-centre gap-1">
                          <User className="h-3 w-3" />
                          <span>{event.eventContactName || event.eventContact?.name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
